import { useMutation, useQuery } from '@tanstack/react-query'
import type { AdminProjectFile } from '@terafile/shared'
import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ModalOverlay } from '../components/ModalOverlay.js'
import {
  getAdminFileDownloadUrl,
  getAdminFileStreamToken,
  getAdminFileThumbnailUrl,
  getAdminTenant,
  listAdminProjectFiles,
  listTenantProjects,
} from '../lib/admin-api.js'
import { FETCH_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { type FileKind, classifyFile, iconForKind } from '../lib/file-kind.js'
import { LOADING, STREAM_STATUS_LABEL } from '../lib/labels.js'
import { useInView } from '../lib/use-in-view.js'

/**
 * 管理者ステルス閲覧: 任意テナントのプロジェクト配下ファイル一覧。
 *
 * - DL ボタン → 署名 URL を取得して別タブで開く
 * - 再生ボタン → Stream トークンを取得して iframe 表示
 * - すべて admin_audit_logs に記録され、テナント側 access_logs には残らない
 */
export function AdminProjectFilesPage() {
  const { id, pid } = useParams<{ id: string; pid: string }>()
  const tenantId = id ?? ''
  const projectId = pid ?? ''
  const [playingIframeUrl, setPlayingIframeUrl] = useState<string | null>(null)

  const filesQ = useQuery({
    queryKey: ['admin-project-files', tenantId, projectId],
    queryFn: () => listAdminProjectFiles(tenantId, projectId),
    enabled: !!tenantId && !!projectId,
  })

  // 「project: <UUID>」のような ID 生表示はユーザーに不親切なので、
  // テナント名 + プロジェクト名を表示するための補助クエリを並行で取得する。
  // 個別 GET エンドポイントは無いので、テナント詳細と当該テナントのプロジェクト一覧から検索する。
  const tenantQ = useQuery({
    queryKey: ['admin-tenant', tenantId],
    queryFn: () => getAdminTenant(tenantId),
    enabled: !!tenantId,
  })
  const projectsQ = useQuery({
    queryKey: ['admin-tenant-projects', tenantId],
    queryFn: () => listTenantProjects(tenantId),
    enabled: !!tenantId,
  })
  const currentProject = projectsQ.data?.items.find((p) => p.id === projectId) ?? null

  const downloadMut = useMutation({
    mutationFn: getAdminFileDownloadUrl,
    onSuccess: (data) => {
      // 別タブで R2 URL を開く（ブラウザが Content-Disposition で DL 開始）
      window.open(data.url, '_blank', 'noopener,noreferrer')
    },
  })

  const playMut = useMutation({
    mutationFn: getAdminFileStreamToken,
    onSuccess: (data) => {
      setPlayingIframeUrl(data.iframeUrl)
    },
  })

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      <div className="bg-red-50 border border-red-300 rounded-md px-4 py-2 text-sm text-red-900 font-medium">
        ⚠ 監査閲覧モード（記録に残らない閲覧）—
        テナント側のアクセス記録には残らず、運営側の監査ログにのみ記録されます
      </div>
      <h1 className="text-xl font-bold">{currentProject?.name ?? 'プロジェクト'}</h1>
      {/* テナント名・プロジェクト名・ファイル件数を人間が読める形で見せる。
            UUID は別途 details で開けばコピーできる（運営サポートで参照する時のため）。 */}
      <p className="text-xs text-muted-foreground">
        {tenantQ.data?.name ?? '読み込み中…'}
        {currentProject && (
          <>
            <span className="mx-1.5">·</span>
            ファイル {currentProject.fileCount} 件{currentProject.archivedAt && ' · アーカイブ済み'}
          </>
        )}
      </p>
      <details className="text-[10px] text-muted-foreground/70">
        <summary className="cursor-pointer hover:text-muted-foreground">内部 ID</summary>
        <p className="font-mono mt-1 break-all">
          tenant: {tenantId}
          <br />
          project: {projectId}
        </p>
      </details>

      {filesQ.isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {filesQ.error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {toUserMessage(filesQ.error, FETCH_ERROR_FALLBACK)}
        </div>
      )}

      {filesQ.data && (
        <ul className="space-y-2">
          {filesQ.data.items.length === 0 ? (
            <li className="text-sm text-muted-foreground">ファイルはありません</li>
          ) : (
            filesQ.data.items.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                onDownload={() => downloadMut.mutate(f.id)}
                onPlay={() => playMut.mutate(f.id)}
                busy={downloadMut.isPending || playMut.isPending}
              />
            ))
          )}
        </ul>
      )}

      {/* mutation エラー */}
      {(downloadMut.error || playMut.error) && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {((downloadMut.error || playMut.error) as Error).message}
        </div>
      )}

      {/* 再生 iframe（モーダル風） */}
      {playingIframeUrl && (
        <ModalOverlay
          className="bg-black/80"
          onBackdropClick={() => setPlayingIframeUrl(null)}
          onEscape={() => setPlayingIframeUrl(null)}
          aria-label="動画プレイヤー"
        >
          <div
            className="bg-black rounded-lg overflow-hidden w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <iframe
              src={playingIframeUrl}
              title="監査閲覧 再生"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
          <button
            type="button"
            onClick={() => setPlayingIframeUrl(null)}
            className="fixed top-4 right-4 text-white text-sm px-3 py-1.5 border border-white/40 rounded z-[51]"
          >
            閉じる
          </button>
        </ModalOverlay>
      )}
    </div>
  )
}

function FileRow({
  file,
  onDownload,
  onPlay,
  busy,
}: {
  file: AdminProjectFile
  onDownload: () => void
  onPlay: () => void
  busy: boolean
}) {
  const kind = classifyFile(file.mimeType)
  const isVideo = kind === 'video'
  const isReady = !file.uploadedAt ? false : isVideo ? file.streamStatus === 'READY' : true
  // サムネ取得時のクリック挙動: 動画は再生、画像は DL（プレビュー用モーダルは admin 側に無いため）
  const onThumbnailClick = isReady && isVideo ? onPlay : file.uploadedAt ? onDownload : null

  return (
    <li className="border border-border rounded-md p-3 bg-background">
      <div className="flex items-center gap-3 flex-wrap">
        <Thumbnail file={file} kind={kind} onClick={onThumbnailClick} />
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate">{file.originalName}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {file.mimeType} · {formatBytes(BigInt(file.sizeBytes))}
            {file.archivedAt && ' · アーカイブ済み'}
            {file.streamStatus && ` · ${STREAM_STATUS_LABEL[file.streamStatus]}`}
            {!file.uploadedAt && ' · アップロード未完了'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isVideo && (
            <button
              type="button"
              onClick={onPlay}
              disabled={!isReady || busy}
              className="text-sm px-3 py-1 border border-border rounded-md disabled:opacity-50 hover:bg-accent/30"
            >
              再生
            </button>
          )}
          <button
            type="button"
            onClick={onDownload}
            disabled={!file.uploadedAt || busy}
            className="text-sm px-3 py-1 border border-border rounded-md disabled:opacity-50 hover:bg-accent/30"
          >
            ダウンロード
          </button>
        </div>
      </div>
    </li>
  )
}

/**
 * ステルス閲覧用のサムネイル。動画 / 画像の場合に画面に入ってから署名 URL を取りに行く。
 *
 * 注意: サムネ URL の発行は admin_audit_logs に毎回記録される（STEALTH_VIEW_THUMBNAIL）。
 * 一覧画面に表示された行ぶんだけログが残る前提で、テナント側 FileList と同じく lazy で発火する。
 */
function Thumbnail({
  file,
  kind,
  onClick,
}: {
  file: AdminProjectFile
  kind: FileKind
  onClick: (() => void) | null
}) {
  const wrapperRef = useRef<HTMLElement>(null)
  const inView = useInView(wrapperRef)

  // 動画: READY のときのみ Stream 署名サムネを取得。READY 以外は変換中なので諦めてアイコン表示。
  // 画像: アップロード完了済みなら原本を fetch。
  const isReadyVideo = kind === 'video' && file.streamStatus === 'READY' && !!file.streamUid
  const isUploadedImage = kind === 'image' && !!file.uploadedAt
  const enabled = inView && (isReadyVideo || isUploadedImage)

  const thumbQ = useQuery({
    queryKey: ['admin-thumbnail', file.id],
    queryFn: () => getAdminFileThumbnailUrl(file.id),
    enabled,
    // Stream 署名サムネは 1h 有効。R2 署名 URL も同等なので 50 分で stale 化させて期限前に再取得を促す。
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
  })

  const wrapperBaseClass =
    'w-24 h-16 rounded-md overflow-hidden flex-shrink-0 relative ring-1 ring-border bg-muted'
  const overlay = kind === 'video' && isReadyVideo ? '▶' : kind === 'image' ? '🔍' : null

  const inner = (
    <>
      {thumbQ.data?.url ? (
        <img
          src={thumbQ.data.url}
          alt=""
          className="w-full h-full object-cover bg-muted"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-2xl">
          {iconForKind(kind)}
        </div>
      )}
      {onClick && overlay && thumbQ.data?.url && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors">
          <span className="opacity-0 hover:opacity-100 text-white text-lg">{overlay}</span>
        </span>
      )}
    </>
  )

  if (onClick) {
    return (
      <button
        ref={wrapperRef as React.RefObject<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        className={`${wrapperBaseClass} hover:ring-foreground/40 cursor-pointer`}
        aria-label={`${file.originalName} を開く`}
      >
        {inner}
      </button>
    )
  }
  return (
    <div ref={wrapperRef as React.RefObject<HTMLDivElement>} className={wrapperBaseClass}>
      {inner}
    </div>
  )
}

function formatBytes(bytes: bigint): string {
  const num = Number(bytes)
  if (num < 1024) return `${num} B`
  if (num < 1024 ** 2) return `${(num / 1024).toFixed(1)} KB`
  if (num < 1024 ** 3) return `${(num / 1024 ** 2).toFixed(1)} MB`
  if (num < 1024 ** 4) return `${(num / 1024 ** 3).toFixed(2)} GB`
  return `${(num / 1024 ** 4).toFixed(2)} TB`
}
